import React, { useState, useEffect } from 'react';
import { lunchboxService } from '../../lib/services/lunchbox';
import LunchboxMenu from './LunchboxMenu';
import LunchboxRecords from './LunchboxRecords';
import AddRecordModal from './AddRecordModal';

const LunchboxCalendar = ({ stats, onBack }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [classFilter, setClassFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ];

  const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const loadDateData = React.useCallback(async (date) => {
    try {
      setLoading(true);
      setError(null);

      const dateStr = formatDate(date);
      const [menu, dateRecords] = await Promise.all([
        lunchboxService.getLunchboxMenu(dateStr),
        lunchboxService.getLunchboxRecords(dateStr)
      ]);

      setMenuData(menu);
      setRecords(dateRecords);
    } catch (err) {
      console.error('Error loading date data:', err);
      setError('載入日期資料時發生錯誤');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) {
      loadDateData(selectedDate);
    }
  }, [selectedDate, loadDateData]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleDateClick = async (date) => {
    if (!date) return;
    setSelectedDate(date);
  };

  const handleAddRecord = async (recordData) => {
    try {
      const newRecord = await lunchboxService.addLunchboxRecord({
        ...recordData,
        date: formatDate(selectedDate)
      });
      
      setRecords(prev => [...prev, newRecord]);
      setShowAddModal(false);
    } catch (err) {
      console.error('Error adding record:', err);
      setError('新增記錄時發生錯誤');
    }
  };

  const getDateStatus = (date) => {
    if (!date) return null;
    
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return 'holiday';
    }
    
    // Check if there are records for this date
    const dateStr = formatDate(date);
    const dateRecords = records.filter(record => record.date === dateStr);
    
    if (dateRecords.length > 0) {
      return 'has-records';
    }
    
    return 'normal';
  };

  const getDateRecords = (date) => {
    if (!date) return [];
    const dateStr = formatDate(date);
    return records.filter(record => record.date === dateStr);
  };

  const filteredRecords = records.filter(record => {
    if (classFilter !== 'all' && record.class_id !== classFilter) {
      return false;
    }
    if (typeFilter !== 'all' && record.lunchbox_type !== typeFilter) {
      return false;
    }
    return true;
  });

  const days = getDaysInMonth(currentDate);

  return (
    <div className="lunchbox-calendar">
      <div className="calendar-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回主頁
        </button>
        <h2>月曆表</h2>
      </div>

      {/* Calendar Navigation */}
      <div className="calendar-navigation">
        <button 
          className="nav-btn"
          onClick={() => navigateMonth(-1)}
        >
          ←
        </button>
        <h3>{currentDate.getFullYear()}年 {monthNames[currentDate.getMonth()]}</h3>
        <button 
          className="nav-btn"
          onClick={() => navigateMonth(1)}
        >
          →
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        <div className="calendar-header-row">
          {dayNames.map(day => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}
        </div>
        
        <div className="calendar-body">
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} className="calendar-day empty"></div>;
            }

            const status = getDateStatus(date);
            const dateRecords = getDateRecords(date);
            const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();

            return (
              <div 
                key={index} 
                className={`calendar-day ${status} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleDateClick(date)}
              >
                <div className="day-number">{date.getDate()}</div>
                {status === 'holiday' && (
                  <div className="day-status holiday">停餐</div>
                )}
                {status === 'has-records' && (
                  <div className="day-status has-menu">有餐單</div>
                )}
                {dateRecords.length > 0 && (
                  <div className="day-records">
                    {dateRecords.slice(0, 2).map((record, idx) => (
                      <span key={idx} className={`record-tag ${record.lunchbox_type.toLowerCase()}`}>
                        {record.class_id}-{record.lunchbox_type}
                      </span>
                    ))}
                    {dateRecords.length > 2 && (
                      <span className="record-count">+{dateRecords.length - 2}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <div className="selected-date-details">
          <div className="date-header">
            <h3>{selectedDate.getFullYear()}年{selectedDate.getMonth() + 1}月{selectedDate.getDate()}日</h3>
            <button 
              className="add-record-btn"
              onClick={() => setShowAddModal(true)}
            >
              新記錄
            </button>
          </div>

          {loading ? (
            <div className="loading-spinner">載入中...</div>
          ) : (
            <>
              <LunchboxMenu 
                menuData={menuData}
                date={selectedDate}
              />
              
              <LunchboxRecords 
                records={filteredRecords}
                classFilter={classFilter}
                typeFilter={typeFilter}
                onClassFilterChange={setClassFilter}
                onTypeFilterChange={setTypeFilter}
                classList={stats.classList}
              />
            </>
          )}
        </div>
      )}

      {/* Add Record Modal */}
      {showAddModal && (
        <AddRecordModal
          date={selectedDate}
          classList={stats.classList}
          onAddRecord={handleAddRecord}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
};

export default LunchboxCalendar;
